import React, { useState } from 'react';
import { MapPin, Info, Users, Heart, UserCircle, Compass, Mountain, Building2, Leaf, Sparkles, Briefcase, ChevronRight, Check, Loader2, ArrowRight } from 'lucide-react';

const TRIP_TYPES = [
  { id: 'leisure', label: 'Leisure & Vacation', icon: Sparkles, color: '#3B82F6', description: 'Relaxation, beaches, resorts, and leisurely exploration' },
  { id: 'adventure', label: 'Adventure & Outdoor', icon: Mountain, color: '#22C55E', description: 'Hiking, water sports, and outdoor activities' },
  { id: 'city', label: 'City & Cultural', icon: Building2, color: '#8B5CF6', description: 'Museums, architecture, nightlife, and urban experiences' },
  { id: 'eco', label: 'Eco-Tourism & Nature', icon: Leaf, color: '#10B981', description: 'Nature reserves, wildlife, and sustainable travel' },
  { id: 'special', label: 'Special Interest', icon: Compass, color: '#F59E0B', description: 'Food & wine tours, wellness retreats, photography' },
  { id: 'business', label: 'Business', icon: Briefcase, color: '#9CA3AF', description: 'Business travel with convenient locations' },
];

const TRAVELER_TYPES = [
  { id: 'family', label: 'Family', icon: Users, color: '#3B82F6', description: 'Kid-friendly areas with activities for all ages' },
  { id: 'couple', label: 'Couple', icon: Heart, color: '#EC4899', description: 'Romantic spots with dining and ambiance' },
  { id: 'friends', label: 'Friends', icon: Users, color: '#8B5CF6', description: 'Social areas with nightlife and group activities' },
  { id: 'solo', label: 'Solo', icon: UserCircle, color: '#F59E0B', description: 'Safe, well-connected areas for solo travelers' },
];

const OptionCard = ({ opt, active, onSelect, onInfo, showInfo, testid }) => (
  <button
    onClick={onSelect}
    className={`relative p-3.5 rounded-2xl text-left transition-all border backdrop-blur-sm ${
      active ? 'bg-white/25 border-white ring-1 ring-white/60' : 'bg-white/10 hover:bg-white/20 border-white/15 hover:border-white/30'
    }`}
    data-testid={testid}
  >
    <div className="flex items-start justify-between mb-2.5">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${opt.color}33` }}>
        <opt.icon size={20} style={{ color: opt.color }} />
      </div>
      {active ? (
        <span className="w-6 h-6 rounded-full bg-white flex items-center justify-center"><Check size={13} className="text-stone-900" /></span>
      ) : (
        <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); onInfo(); }} className="w-6 h-6 rounded-full bg-white/15 hover:bg-white/30 flex items-center justify-center cursor-pointer">
          <Info size={12} className="text-white/80" />
        </span>
      )}
    </div>
    <div className="text-white font-medium text-sm">{opt.label}</div>
    {showInfo && <div className="mt-1.5 text-xs text-white/70">{opt.description}</div>}
  </button>
);

// Inline selector rendered over the spinning globe (glass panel, no full-screen takeover).
export const DestinationFlow = ({ city, onComplete, onClose }) => {
  const [trip, setTrip] = useState(null);
  const [traveler, setTraveler] = useState(null);
  const [info, setInfo] = useState(null);
  const [zooming, setZooming] = useState(false);

  const submit = () => {
    if (!trip || !traveler) return;
    setZooming(true);
    setTimeout(() => onComplete(trip.id, traveler.id), 900);
  };

  if (zooming) {
    return (
      <div className="flex flex-col items-center gap-3 py-16" data-testid="flow-zooming">
        <Loader2 className="w-10 h-10 text-white animate-spin" />
        <span className="text-white text-base font-medium">Flying to {city.name}…</span>
        <span className="text-white/70 text-sm">{trip.label} • {traveler.label}</span>
      </div>
    );
  }

  return (
    <div
      className="w-full max-w-3xl mx-auto text-left bg-stone-950/55 backdrop-blur-xl border border-white/10 rounded-3xl p-6 md:p-8 shadow-2xl"
      data-testid="destination-flow"
    >
      <button onClick={onClose} className="flex items-center gap-1.5 text-white/70 hover:text-white mb-5 transition-colors" data-testid="back-to-search">
        <ChevronRight size={18} className="rotate-180" /> <span className="text-sm">Back to search</span>
      </button>

      {/* Destination card */}
      <div className="flex items-center gap-3 mb-7 p-4 bg-white/10 rounded-2xl border border-white/15">
        <div className="w-11 h-11 bg-blue-500/30 rounded-xl flex items-center justify-center">
          <MapPin className="w-5 h-5 text-blue-300" />
        </div>
        <div>
          <div className="text-white font-semibold text-lg">{city.name}</div>
          <div className="text-white/60 text-sm">{city.country}</div>
        </div>
      </div>

      {/* Trip type */}
      <h3 className="font-serif text-2xl text-white mb-1">What type of trip?</h3>
      <p className="text-white/60 text-sm mb-4">Select your trip style for personalized recommendations</p>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
        {TRIP_TYPES.map((t) => (
          <OptionCard
            key={t.id} opt={t} active={trip?.id === t.id}
            onSelect={() => setTrip(t)}
            onInfo={() => setInfo(info === `trip-${t.id}` ? null : `trip-${t.id}`)}
            showInfo={info === `trip-${t.id}`}
            testid={`trip-type-${t.id}`}
          />
        ))}
      </div>

      {/* Travelers */}
      <h3 className="font-serif text-2xl text-white mb-1">Who is going?</h3>
      <p className="text-white/60 text-sm mb-4">We'll find the perfect areas for your group</p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        {TRAVELER_TYPES.map((t) => (
          <OptionCard
            key={t.id} opt={t} active={traveler?.id === t.id}
            onSelect={() => setTraveler(t)}
            onInfo={() => setInfo(info === `trav-${t.id}` ? null : `trav-${t.id}`)}
            showInfo={info === `trav-${t.id}`}
            testid={`traveler-type-${t.id}`}
          />
        ))}
      </div>

      {/* Submit */}
      <button
        onClick={submit}
        disabled={!trip || !traveler}
        className={`w-full py-4 rounded-2xl font-semibold flex items-center justify-center gap-2 transition-all ${
          trip && traveler ? 'bg-white text-stone-900 hover:bg-stone-100' : 'bg-white/10 text-white/40 cursor-not-allowed'
        }`}
        data-testid="flow-submit"
      >
        {trip && traveler ? <>Show me where to stay in {city.name} <ArrowRight size={18} /></> : 'Select a trip type and travelers'}
      </button>
    </div>
  );
};
